import { useState, useEffect } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import PlatformShell from '../../components/layout/PlatformShell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import { usePlatformMsps } from '../../hooks/usePlatformData';

const inputStyle = {
  width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
  padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
};

const STATUS_BADGE = (ok) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 9,
  fontWeight: 700, fontFamily: FONTS.mono, letterSpacing: '0.04em',
  background: ok ? `${C.grn}15` : `${C.amb}15`,
  color: ok ? C.grn : C.amb,
  border: `1px solid ${ok ? C.grn : C.amb}33`,
});

function SecretsCard() {
  const api = useApiClient();
  const [secrets, setSecrets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editingSecret, setEditingSecret] = useState(null);
  const [secretValue, setSecretValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchSecrets = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.get('/platform/secrets/status');
      setSecrets(data);
    } catch (err) { setLoadError(err.message || 'Failed to load secrets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSecrets(); }, []);

  const showToast = (msg, isError) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSaveSecret = async (name) => {
    if (!secretValue.trim()) return;
    setSaving(true);
    try {
      await api.put(`/platform/secrets/${name}`, { value: secretValue });
      setSecrets(prev => ({ ...prev, [name]: { configured: true, hasValue: true } }));
      setEditingSecret(null);
      setSecretValue('');
      showToast(`Secret "${friendlyName(name)}" updated.`, false);
    } catch (err) {
      showToast(err.message || 'Failed to save secret.', true);
    } finally {
      setSaving(false);
    }
  };

  const friendlyName = (key) => {
    const last = key.split('/').pop();
    return last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Integration API Keys</div>
        {[1,2,3,4].map(i => <Skeleton key={i} height={18} style={{ marginBottom: 10 }} />)}
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Integration API Keys</div>
        <PageError error={loadError} onRetry={fetchSecrets} />
      </Card>
    );
  }

  return (
    <Card style={{ padding: 22 }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: toast.isError ? C.redBg : C.grnBg,
          border: `1px solid ${toast.isError ? C.red : C.grn}33`,
          color: toast.isError ? C.red : C.grn,
          fontSize: 12, fontFamily: FONTS.mono, fontWeight: 600,
        }}>{toast.msg}</div>
      )}
      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 6 }}>Integration API Keys</div>
      <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>
        These secrets are stored in AWS Secrets Manager and used for platform-wide breach intelligence and OSINT scanning.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {secrets && Object.entries(secrets).map(([key, status]) => (
          <div key={key} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <span style={{ fontSize: 11, fontFamily: FONTS.mono, color: C.t1, fontWeight: 600 }}>
                {friendlyName(key)}
              </span>
              <span style={STATUS_BADGE(status.configured)}>
                {status.configured ? 'CONFIGURED' : 'NOT SET'}
              </span>
            </div>

            {editingSecret === key ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="password"
                  value={secretValue}
                  onChange={e => setSecretValue(e.target.value)}
                  placeholder="Enter API key..."
                  style={{ ...inputStyle, width: 220, fontSize: 11 }}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveSecret(key); if (e.key === 'Escape') { setEditingSecret(null); setSecretValue(''); } }}
                />
                <Button variant="pri" onClick={() => handleSaveSecret(key)} style={{ fontSize: 10, padding: '5px 12px', opacity: saving ? 0.6 : 1 }}>
                  {saving ? '...' : 'Save'}
                </Button>
                <Button variant="ghost" onClick={() => { setEditingSecret(null); setSecretValue(''); }} style={{ fontSize: 10, padding: '5px 8px' }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="sec" onClick={() => { setEditingSecret(key); setSecretValue(''); }} style={{ fontSize: 10, padding: '4px 10px' }}>
                {status.configured ? 'Update' : 'Set Key'}
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function MspBrandingOverview() {
  const {
    data: msps,
    loading,
    refreshing,
    isStale,
    error: loadError,
    refetch,
  } = usePlatformMsps();

  if (loading) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Managed MSPs</div>
        {[1,2].map(i => <Skeleton key={i} height={48} style={{ marginBottom: 10 }} />)}
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Managed MSPs</div>
        <PageError error={loadError} onRetry={refetch} />
      </Card>
    );
  }

  return (
    <Card style={{ padding: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 6 }}>Managed MSPs</div>
      <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>
        Overview of onboarded MSP partners and their branding/domain configuration status.
      </div>
      {(refreshing || isStale) && (
        <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 6, background: `${C.sky}10`, border: `1px solid ${C.sky}25`, color: C.t2, fontFamily: FONTS.mono, fontSize: 10 }}>
          Showing cached MSP partner details while the platform refreshes the directory.
        </div>
      )}

      {(!msps || msps.length === 0) ? (
        <div style={{ padding: 20, textAlign: 'center', color: C.t3, fontFamily: FONTS.mono, fontSize: 11 }}>
          No MSPs onboarded yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {msps.map(msp => (
            <div key={msp.mspId} style={{
              padding: '14px 16px', borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 4 }}>
                  {msp.name}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontFamily: FONTS.mono, color: C.t2 }}>
                    {msp.clientCount || 0} client{(msp.clientCount || 0) !== 1 ? 's' : ''}
                  </span>
                  <span style={STATUS_BADGE(msp.isActive !== false)}>
                    {msp.isActive !== false ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                  {msp.partnerTier && (
                    <span style={{
                      fontSize: 9, fontFamily: FONTS.mono, padding: '2px 6px', borderRadius: 4,
                      background: `${C.sky}12`, color: C.sky, border: `1px solid ${C.sky}33`,
                    }}>{msp.partnerTier}</span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {msp.avgScore != null && (
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: FONTS.mono, color: msp.avgScore >= 70 ? C.grn : msp.avgScore >= 40 ? C.amb : C.red }}>
                    {Math.round(msp.avgScore)}
                  </div>
                )}
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>avg score</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SystemHealthCard() {
  const api = useApiClient();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fetchSummary = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.get('/platform/summary');
      setSummary(data);
    } catch (err) { setLoadError(err.message || 'Failed to load summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, []);

  if (loading) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Platform Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[1,2,3].map(i => <Skeleton key={i} height={60} />)}
        </div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card style={{ padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Platform Summary</div>
        <PageError error={loadError} onRetry={fetchSummary} />
      </Card>
    );
  }

  const stats = summary ? [
    { label: 'Active MSPs', value: summary.activeMsps, total: summary.totalMsps, color: C.sky },
    { label: 'Total Clients', value: summary.totalClients, color: C.pur },
    { label: 'Total Findings', value: summary.totalFindings?.toLocaleString(), color: C.amb },
    { label: 'Avg Security Score', value: summary.avgScoreAcrossAllClients || 'N/A', color: C.grn },
    { label: 'MSPs Added (30d)', value: summary.mspsAddedThisMonth, color: C.sky },
    { label: 'Scans Run (30d)', value: summary.scansRunThisMonth, color: C.pur },
  ] : [];

  return (
    <Card style={{ padding: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 16 }}>Platform Summary</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {stats.map(s => (
          <div key={s.label} style={{
            padding: '14px 16px', borderRadius: 7, background: C.s2, border: `1px solid ${C.b1}`,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: FONTS.mono, color: s.color, marginBottom: 4 }}>
              {s.value}{s.total != null ? <span style={{ fontSize: 12, color: C.t3 }}>/{s.total}</span> : null}
            </div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SmtpStatusCard() {
  return (
    <Card style={{ padding: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 6 }}>Transactional Email (SMTP)</div>
      <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 14 }}>
        System email configuration for password resets, team invites, and notifications.
        These are set via Azure App Settings and not configurable from the portal UI.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { label: 'Smtp__Host', desc: 'SMTP relay hostname (e.g. email-smtp.us-east-1.amazonaws.com)' },
          { label: 'Smtp__Port', desc: 'SMTP port (typically 587 for TLS)' },
          { label: 'Smtp__Username', desc: 'SMTP authentication username' },
          { label: 'Smtp__Password', desc: 'SMTP authentication password' },
          { label: 'Smtp__FromAddress', desc: 'Sender email address' },
          { label: 'Smtp__FromName', desc: 'Sender display name' },
          { label: 'PortalBaseUrl', desc: 'Base URL for links in transactional emails' },
        ].map(item => (
          <div key={item.label} style={{
            padding: '8px 12px', borderRadius: 6, background: C.s2, border: `1px solid ${C.b1}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <span style={{ fontSize: 11, fontFamily: FONTS.mono, color: C.t1, fontWeight: 600 }}>{item.label}</span>
              <span style={{ fontSize: 9, fontFamily: FONTS.mono, color: C.t3, marginLeft: 8 }}>{item.desc}</span>
            </div>
            <span style={{
              fontSize: 9, fontFamily: FONTS.mono, padding: '2px 8px', borderRadius: 4,
              background: `${C.t3}10`, color: C.t3, border: `1px solid ${C.t3}22`,
            }}>AZURE APP SETTING</span>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: 14, padding: '10px 14px', borderRadius: 7,
        background: `${C.sky}08`, border: `1px solid ${C.sky}22`,
        fontSize: 10, color: C.sky, fontFamily: FONTS.mono, lineHeight: 1.6,
      }}>
        To configure SMTP, set these values in Azure Portal &rarr; App Service &rarr; Configuration &rarr; Application Settings.
        For AWS SES, verify the sending domain first, then create SMTP credentials in the SES console.
      </div>
    </Card>
  );
}

export default function PlatformSettings() {
  return (
    <PlatformShell title="Platform Settings" sub="System configuration and integration management">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SystemHealthCard />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <SecretsCard />
          <MspBrandingOverview />
        </div>
        <SmtpStatusCard />
      </div>
    </PlatformShell>
  );
}
